import { ROUTES } from '@/constants/routes';
import authService from '@/modules/auth/services/authService';
import { useAuthStore } from '@/modules/auth/store/authStore';

import router from '@/router';

let refreshPromise: Promise<string> | null = null;

/**
 * Shared mutex-based refresh. All callers (interceptor, store timer,
 * visibility handler) funnel through this single promise so that only
 * one refresh-token call is in flight at any time.
 */
export const ensureFreshToken = async (): Promise<string> => {
	if (refreshPromise) return refreshPromise;

	refreshPromise = (async () => {
		try {
			const tokens = authService.getTokens();
			if (!tokens) throw new Error('No tokens available');

			const loginResponse = await authService.refreshToken(
				tokens.refresh_token
			);
			authService.setTokens({ ...loginResponse, issued_at: Date.now() });
			return loginResponse.access_token;
		} catch (error) {
			await forceLogout();
			throw error;
		} finally {
			refreshPromise = null;
		}
	})();

	return refreshPromise;
};

/**
 * Centralised forced logout used by interceptors, store timer, and
 * multi-tab sync. Cleans both localStorage and Pinia state.
 */
export const forceLogout = async () => {
	try {
		const authStore = useAuthStore();
		authStore.logoutSuccess();
	} catch {
		authService.removeTokens();
	}

	const currentRoute = router.currentRoute.value;
	if (currentRoute.name !== ROUTES.LOGIN) {
		router.push({
			name: ROUTES.LOGIN,
			query: { redirect: currentRoute.fullPath },
		});
	}
};
